import { JobSearchComponent } from "@container/job-search-component";
import { JobCard } from "@container/job-card";
import { Job, JobSearchParams } from "@type/job";
import { useEffect, useState } from "react";
import { findJobs } from "@service/job";
import { Button } from "@component/button";
import { MetaData } from "@type/common";
import { useNavigate } from "react-router-dom";
import { defaultPagination } from "@constant/app";
import { ClockIcon } from "lucide-react";
import LoadingBox from "@component/loading-box";
import { useUserProfileContext } from "@context/user_profile";

export default function JobMatcher() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [, userInfo] = useUserProfileContext();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [metadata, setMetadata] = useState<MetaData>({
    pagination: {
      page: defaultPagination.page,
      limit: defaultPagination.limit,
      total: 0,
      total_pages: 0,
    },
  });
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<JobSearchParams>({
    keyword: "",
    page: defaultPagination.page,
    top_k: defaultPagination.limit,
  });
  const [filterJob, setFilterJob] = useState<string>("all_jobs")

  async function fetchJobs(params: JobSearchParams, isLoadMore = false) {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      if (filterJob === "matched_job") {
        params.MatchOnly = true;
      } else {
        params.MatchOnly = false;
      }

      if (filterJob === "invited") {
        params.invited_only = true;
      } else {
        params.invited_only = false;
      }

      if (filterJob === "closed_jobs") {
        params.closed_only = true;
      } else {
        params.closed_only = false;
      }
      params.filter_newest = true;

      setError(null);
      const response = await findJobs(params);

      if (isLoadMore) {
        setJobs((prevJobs: Job[]) => {
          return [...prevJobs, ...response.data];
        });
      } else {
        setJobs(response.data);
      }
      setMetadata(response.meta);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to load job listings.");
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    const tab = params.get("tab");
    if (tab) {
      setFilterJob(tab);
    }
  }, [params.get("tab")])

  useEffect(() => {
    fetchJobs(searchParams);
  }, [filterJob])

  useEffect(() => {
    if (userInfo && userInfo.referral_code === import.meta.env.VITE_REFERRAL_CODE_COCACOLA) {
      setFilterJob("all_jobs");
    }
  }, [userInfo])

  // Handler for search component to update search parameters
  const handleSearch = (params: JobSearchParams, callback: VoidFunction) => {
    setCurrentPage(defaultPagination.page);
    setSearchParams({
      ...params,
      page: defaultPagination.page,
      top_k: defaultPagination.limit,
    });
    fetchJobs({
      ...params,
      page: defaultPagination.page,
      top_k: defaultPagination.limit,
    });
    callback();
  };

  // Handler for resetting the search and fetching all jobs
  const handleReset = () => {
    setCurrentPage(defaultPagination.page);
    setSearchParams({
      keyword: "",
      page: defaultPagination.page,
      top_k: defaultPagination.limit,
    });
    fetchJobs({
      keyword: "",
      page: defaultPagination.page,
      top_k: defaultPagination.limit,
    });
  };

  // Handler for loading more jobs
  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setSearchParams((prev: JobSearchParams) => {
      return {
        ...prev,
        page: nextPage,
      };
    });

    await fetchJobs(
      {
        ...searchParams,
        page: nextPage,
        top_k: defaultPagination.limit,
        MatchOnly: filterJob === "matched_job",
        invited_only: filterJob === "invited",
      },
      true
    );
  };

  const handleTabChange = (tab: string) => {
    setFilterJob(tab);
    // add query params by tab ?tab=${tab}
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.pushState({}, "", url.toString());
  }

  // Otherwise show the job listings view
  return (
    <div
    // className="bg-white rounded-lg p-6 border border-gray-200"
    >
      <div className="mb-3 sm:mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Job Matches</h2>
        <p className="text-gray-600">
          Opportunities that match your skills and experience.
        </p>
      </div>

      {/* Job Search Component */}
      <div className="w-full">
        <JobSearchComponent onSearch={handleSearch} onReset={handleReset} />
      </div>

      <div className="flex space-x-2">
        {/* <button
          onClick={() => handleTabChange("matched_job")}
          className={`border border-black hover:bg-blue-600 hover:text-white dark:bg-blue-900/30 text-black dark:text-blue-300 px-3 py-1 rounded-full text-sm ${filterJob === "matched_job" ? "bg-blue-600 text-white border-blue-800 hover:bg-blue-800" : ""}`}
        >
          Matched Jobs
        </button> */}
        <button
          onClick={() => handleTabChange("all_jobs")}
          className={`border border-black hover:bg-blue-600 hover:text-white dark:bg-blue-900/30 text-black dark:text-blue-300 px-3 py-1 rounded-full text-sm ${filterJob === "all_jobs" ? "bg-blue-600 text-white border-blue-800 hover:bg-blue-800" : ""}`}
        >
          All Jobs
        </button>
        <button
          onClick={() => handleTabChange("invited")}
          className={`border border-black hover:bg-blue-600 hover:text-white dark:bg-blue-900/30 text-black dark:text-blue-300 px-3 py-1 rounded-full text-sm ${filterJob === "invited" ? "bg-blue-600 text-white border-blue-800 hover:bg-blue-800" : ""}`}
        >
          Invited Jobs
        </button>
        {userInfo?.referral_code === import.meta.env.VITE_REFERRAL_CODE_COCACOLA && (
          <button
            onClick={() => handleTabChange("closed_jobs")}
            className={`border border-black hover:bg-blue-600 hover:text-white dark:bg-blue-900/30 text-black dark:text-blue-300 px-3 py-1 rounded-full text-sm ${filterJob === "closed_jobs" ? "bg-blue-600 text-white border-blue-800 hover:bg-blue-800" : ""}`}
          >
            Closed Jobs
          </button>
        )}

      </div>

      {filterJob == "invited" && (
        <div
          className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-4 flex items-center justify-between mt-4"
        >
          <div className="flex items-center">
            <div
              className="bg-blue-100 rounded-full p-1.5 mr-2 flex-shrink-0 hidden sm:block"
              style={{
                paddingTop: "10px",
                paddingRight: "10px",
                paddingBottom: "10px",
                paddingLeft: "10px",
              }}
            >
              <ClockIcon
                className="h-4 w-4 text-blue-600"
                style={{ color: "#2563eb" }}
              />
            </div>

            <p className="text-sm" style={{ color: "#09090b" }}>
              Job invitations directly from companies will expire in 7 days and will
              be automatically removed after no further action.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <LoadingBox />
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md my-4">
          {error}
        </div>
      )}

      {/* Job Listings */}
      {!isLoading && !error && (
        <>
          <div className="space-y-4 mt-4">
            {jobs.length > 0 ? (
              jobs.map((job: Job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => navigate(`/job/${job.id}`)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No job listings found. Try adjusting your search criteria.
              </div>
            )}
          </div>
          {jobs.length > 0 &&
            currentPage != metadata.pagination.total_pages && (
              <div className="mt-6 flex justify-center">
                <Button
                  variant="outline"
                  className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                      Loading...
                    </div>
                  ) : (
                    "Load More Jobs"
                  )}
                </Button>
              </div>
            )}
        </>
      )}
    </div>
  );
}
